import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { toDecimalString } from '../utils/decimal-places.util';

export interface Parts {
  integer: string;
  decimal: string;
}

@Pipe({
  name: 'splitParts',
  standalone: true,
})
export class SplitPartsPipe implements PipeTransform {
  private locale = inject(LOCALE_ID);
  private formatters = new Map<string, Intl.NumberFormat>();
  private decimalSeparator = new Intl.NumberFormat(this.locale, { minimumFractionDigits: 1 })
    .formatToParts(1.1)
    .find((p) => p.type === 'decimal')?.value;
  private localeDigits = Array.from({ length: 10 }, (_, digit) => new Intl.NumberFormat(this.locale).format(digit));

  transform(value: number | string | null | undefined, precision = 0, currency?: string): Parts {
    const exact = value === null || value === undefined ? null : toDecimalString(value);
    if (exact === null) {
      return { integer: '0', decimal: '' };
    }

    const hasCurrency = !!currency && currency.trim().length > 0;
    const negative = exact.startsWith('-');
    const [integerDigits, fractionDigits = ''] = exact.replace('-', '').split('.');
    const truncated = fractionDigits.slice(0, precision);
    const fraction = hasCurrency ? truncated.padEnd(precision, '0') : truncated.replace(/0+$/, '');

    const integerValue = BigInt(integerDigits);
    const parts = this.formatterFor(hasCurrency ? currency : '').formatToParts(
      negative ? (integerValue === 0n ? -0 : -integerValue) : integerValue,
    );
    const lastInteger = parts.map((part) => part.type).lastIndexOf('integer');
    const integer = parts
      .slice(0, lastInteger + 1)
      .map((part) => part.value)
      .join('');
    const suffix = parts
      .slice(lastInteger + 1)
      .map((part) => part.value)
      .join('');

    if (!this.decimalSeparator || precision === 0 || !fraction) {
      return {
        integer: integer + suffix,
        decimal: '',
      };
    }

    return {
      integer,
      decimal: this.decimalSeparator + fraction.replace(/\d/g, (digit) => this.localeDigits[+digit]) + suffix,
    };
  }

  private formatterFor(currency: string): Intl.NumberFormat {
    let formatter = this.formatters.get(currency);
    if (!formatter) {
      formatter = new Intl.NumberFormat(this.locale, {
        style: currency ? 'currency' : 'decimal',
        ...(currency && { currency }),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      this.formatters.set(currency, formatter);
    }
    return formatter;
  }
}
